import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Badge,
  Flex,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { 
  collection, 
  doc, 
  getDocs,
  updateDoc, 
  query,
  where,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { Match } from '../../types';

interface MatchWithResults extends Match {
  tempScoreA?: number;
  tempScoreB?: number;
  }

const ManageResults: React.FC = () => {
  const [activeMatches, setActiveMatches] = useState<MatchWithResults[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadActiveMatches();
  }, []);

  const loadActiveMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const q = query(
        matchesRef,
        where('activo', '==', true),
        orderBy('date', 'asc')
      );
      const matchesSnap = await getDocs(q);
      const matchesData = matchesSnap.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        id: doc.id,
        ...doc.data(),
        tempScoreA: doc.data().score_a,
        tempScoreB: doc.data().score_b
      })) as MatchWithResults[];

      setActiveMatches(matchesData);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los partidos activos',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleScoreChange = (matchId: string, field: 'tempScoreA' | 'tempScoreB', value: number) => {
    setActiveMatches(prevMatches => 
      prevMatches.map(match => 
        match.id === matchId 
          ? { ...match, [field]: value }
          : match
      )
    );
  };

  const handleSaveResults = async (matchId: string) => {
    const match = activeMatches.find(m => m.id === matchId);
    if (!match) return;

    if (match.tempScoreA === undefined || match.tempScoreB === undefined) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa ambos resultados',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const matchRef = doc(db, 'matches', matchId);

      // Verifica si ya existe resultado
      const yaExisteResultado = match.score_a !== null && match.score_b !== null;

      await updateDoc(matchRef, {
        score_a: match.tempScoreA,
        score_b: match.tempScoreB,
        updated_at: new Date()
      });

      toast({
        title: 'Éxito',
        description: yaExisteResultado
          ? 'Resultado actualizado correctamente'
          : 'Resultado guardado correctamente',
        status: 'success',
        duration: 3000,
      });

      // Recargar partidos activos
      loadActiveMatches();
    } catch (error) {
      console.error('Error al guardar resultado:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el resultado',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={4}>
        <Heading>Administración de Resultados</Heading>
        
      <Card>
        <CardHeader>
          <Heading size="md">Partidos Activos</Heading>
        </CardHeader>
        <CardBody>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Fase</Th>
              <Th>Local</Th>
              <Th>Visitante</Th>
                <Th>Resultado Local</Th>
                <Th>Resultado Visitante</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
              {activeMatches.map((match) => (
                <Tr key={match.id}>
                  <Td>{match.phase}</Td>
                  <Td>{match.team_a}</Td>
                  <Td>{match.team_b}</Td>
                  <Td>
                    <NumberInput
                      min={0}
                      max={20}
                      value={match.tempScoreA ?? ''}
                      onChange={(_, value) => handleScoreChange(match.id, 'tempScoreA', value)}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Td>
                  <Td>
                    <NumberInput
                      min={0}
                      max={20}
                      value={match.tempScoreB ?? ''}
                      onChange={(_, value) => handleScoreChange(match.id, 'tempScoreB', value)}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Td>
                  <Td>
                    <Button
                      colorScheme="blue"
                      size="sm"
                      onClick={() => handleSaveResults(match.id)}
                      isLoading={loading}
                    >
                      Guardar Resultado
                    </Button>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
        </CardBody>
      </Card>
      </VStack>
  );
};

export default ManageResults; 